import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CalendarDays, ChevronDown, Plus } from 'lucide-react'

const dataEntryOptions = [
  { key: 'enterDailyData', to: '/customer/data-entry/daily' },
  { key: 'enterWeeklyData', to: '/customer/data-entry/weekly' },
  { key: 'enterData', to: '/customer/data-entry' },
]

const ADD_MENU_CLOSE_DELAY_MS = 250

const DashboardOverviewHeader = ({
  businessName,
  selectedMonthKey,
  monthOptions,
  onMonthChange,
  onDeleteData = () => {},
}) => {
  const { t } = useTranslation('dashboard')
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false)
  const menuRootRef = useRef(null)
  const addButtonRef = useRef(null)
  const suppressFocusOpenRef = useRef(false)
  const menuCloseTimeoutRef = useRef(null)

  useEffect(() => () => {
    if (menuCloseTimeoutRef.current) clearTimeout(menuCloseTimeoutRef.current)
  }, [])

  useEffect(() => {
    if (!isAddMenuOpen) return undefined

    const closeOnOutsidePointer = (event) => {
      if (!menuRootRef.current?.contains(event.target)) setIsAddMenuOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer)
  }, [isAddMenuOpen])

  const closeMenuIfFocusLeaves = (event) => {
    if (!menuRootRef.current?.contains(event.relatedTarget)) setIsAddMenuOpen(false)
  }

  const handleMenuKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      setIsAddMenuOpen(false)
      if (document.activeElement !== addButtonRef.current) {
        suppressFocusOpenRef.current = true
        addButtonRef.current?.focus()
      }
    }
  }

  const handleFocusCapture = () => {
    if (suppressFocusOpenRef.current) {
      suppressFocusOpenRef.current = false
      return
    }
    setIsAddMenuOpen(true)
  }

  const cancelPendingMenuClose = () => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }
  }

  const handleMenuMouseEnter = () => {
    cancelPendingMenuClose()
    setIsAddMenuOpen(true)
  }

  const handleMouseLeave = () => {
    cancelPendingMenuClose()
    menuCloseTimeoutRef.current = setTimeout(() => {
      menuCloseTimeoutRef.current = null
      if (!menuRootRef.current?.contains(document.activeElement)) setIsAddMenuOpen(false)
    }, ADD_MENU_CLOSE_DELAY_MS)
  }

  return (
    <section className="relative rounded-2xl border border-gray-200 bg-white px-5 py-5 shadow-sm sm:px-8 sm:py-6">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-[#E1174C]">
            {t('customer.dashboardEyebrow')}
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            {t('customer.dashboardGreeting', {
              businessName: businessName || t('customer.businessFallback'),
            })}
          </h1>
          <p className="mt-2 text-sm text-gray-600 sm:text-base">
            {t('customer.dashboardOverviewDescription')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 lg:justify-end">
          <label className="relative min-w-[148px] flex-1 sm:flex-none">
            <span className="sr-only">{t('customer.dashboardMonthLabel')}</span>
            <select
              aria-label={t('customer.dashboardMonthLabel')}
              value={selectedMonthKey}
              onChange={(event) => onMonthChange(event.target.value)}
              className="h-11 w-full appearance-none rounded-xl border border-gray-200 bg-white py-2 pl-4 pr-10 text-sm font-semibold text-gray-700 outline-none transition focus:border-[#FF1B54] focus:ring-2 focus:ring-[#FF1B54]/20"
            >
              {monthOptions.map((option) => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
            />
          </label>

          <div
            ref={menuRootRef}
            className="relative flex-1 sm:flex-none"
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocusCapture={handleFocusCapture}
            onBlurCapture={closeMenuIfFocusLeaves}
            onKeyDown={handleMenuKeyDown}
          >
            <button
              ref={addButtonRef}
              type="button"
              aria-haspopup="true"
              aria-expanded={isAddMenuOpen}
              aria-controls="dashboard-add-data-menu"
              onClick={(event) => {
                const supportsHover = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
                if (supportsHover && event.detail > 0) {
                  setIsAddMenuOpen(true)
                } else {
                  setIsAddMenuOpen((open) => !open)
                }
              }}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#FF1B54] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#E1174C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2 sm:w-auto"
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('customer.addData')}
            </button>

            {isAddMenuOpen && (
              <div
                id="dashboard-add-data-menu"
                role="group"
                aria-label={t('customer.addDataMenuLabel')}
                className="absolute left-0 top-full z-50 mt-2 w-max min-w-[224px] rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg shadow-gray-900/10"
              >
                {dataEntryOptions.map((option) => (
                  <Link
                    key={option.key}
                    to={option.to}
                    onClick={() => setIsAddMenuOpen(false)}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-[#FFF1F4] hover:text-[#E1174C] focus:bg-[#FFF1F4] focus:text-[#E1174C] focus:outline-none"
                  >
                    {t(`customer.${option.key}`)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onDeleteData}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#FFD0DC] bg-[#FFF5F7] px-4 text-sm font-semibold text-[#C91545] transition-colors hover:border-[#FF1B54] hover:bg-[#FFE5EC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2 sm:w-auto"
          >
            <CalendarDays aria-hidden="true" className="h-4 w-4 text-gray-500" />
            {t('customer.deleteData')}
          </button>
        </div>
      </div>
    </section>
  )
}

export default DashboardOverviewHeader
